import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const cat = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '奶妈动漫',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />

      <main className="pt-20">
        <article className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: cat?.name || '', path: `/category/${cat?.slug}` },
                { name: article.title },
              ]}
            />

            <div className="mt-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {cat && (
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-xs font-medium text-primary-500 bg-primary-100/70 px-2 py-1 rounded-md hover:bg-primary-200/70 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                )}
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-md whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 mb-8 pb-8 border-b border-background-200/70">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-500">
                    {article.date} · {article.readTime}
                  </p>
                </div>
              </div>

              <div className="relative w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-foreground-600 bg-secondary-100 px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h3 className="font-heading text-xl text-foreground-900 mb-8">相关推荐</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => {
                  const relatedCat = categories.find((c) => c.id === related.category);
                  return (
                    <ArticleCard
                      key={related.id}
                      article={related}
                      categorySlug={relatedCat?.slug || ''}
                      categoryName={relatedCat?.name || ''}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}